import { useMemo } from "react";

import moment, { type MomentSetObject } from "moment";

import type { Clock } from "~/v2/api/rest/data";
import {
  EQUITY_TIME_IN_FORCE_OPTIONS,
  type OrderType,
  type QuantityType,
  TOKENIZED_EQUITY_TIME_IN_FORCE_OPTIONS,
} from "~/v2/constants/equityOrder";

const EASTERN_TIMEZONE = "America/New_York";
const EXTENDED_HOURS_CLOSE: MomentSetObject = { hour: 20, minute: 0 }; // 8:00 PM

/**
 * @todo - Design question
 * When order type is "limit" we allow setting extend_hours: true
 * But the only valid timeInForce for limit orders with extended hours is "day"
 *
 * Should "day" be the only time in force option we show when order is "limit" and extend_hours is true?
 */

interface UseTimeInForceOptionsProps {
  orderType: OrderType;
  quantityType: QuantityType;
  isFractional: boolean;
  clock: Clock | undefined;
  isExtendedHours: boolean;
  isTokenizedEquity?: boolean;
}

export const getTimeInForceOptions = ({
  orderType,
  quantityType,
  isFractional,
  clock,
  isExtendedHours,
  isTokenizedEquity = false,
}: UseTimeInForceOptionsProps) => {
  if (isTokenizedEquity) {
    return TOKENIZED_EQUITY_TIME_IN_FORCE_OPTIONS.filter((timeInForce) =>
      timeInForce.orderTypes.includes(orderType),
    ).map(({ label, subLabel, value }) => ({ label, subLabel, value }));
  }

  return (
    EQUITY_TIME_IN_FORCE_OPTIONS.filter((timeInForce) =>
      // Order type supports TIF
      timeInForce.orderTypes.includes(orderType) &&
      // TIF is valid for extended hours
      isExtendedHours
        ? timeInForce.extendedHoursOrderTypes.includes(orderType)
        : true &&
          // When quantityType is in dollars only allow "day" timeInForce
          (quantityType === "dollars" || isFractional
            ? timeInForce.value === "day"
            : true),
    )
      // Create labels
      .map(({ label, subLabel, value }) => {
        if (value === "day") {
          if (clock) {
            const now = moment().tz(EASTERN_TIMEZONE);
            const nextClose = moment.tz(clock.next_close, EASTERN_TIMEZONE);

            if (isExtendedHours) {
              // Extended hours close at 8 PM ET
              const extendedClose = moment
                .tz(clock.next_close, EASTERN_TIMEZONE)
                .set(EXTENDED_HOURS_CLOSE);
              const isBeforeExtendedClose = now.isBefore(extendedClose);
              const isSameDay = now.isSame(extendedClose, "day");

              if (isSameDay && isBeforeExtendedClose) {
                return {
                  label,
                  subLabel: `Expires at ${extendedClose.format("h:mm a")} ET today`,
                  value,
                };
              }

              // If we're past extended hours close, show next trading day
              const nextTradingDayExtendedClose = moment
                .tz(clock.next_open, EASTERN_TIMEZONE)
                .set(EXTENDED_HOURS_CLOSE);

              return {
                label,
                subLabel: `Expires ${nextTradingDayExtendedClose.format("MM/DD")} at ${nextTradingDayExtendedClose.format("h:mm a")} ET`,
                value,
              };
            }

            if (clock.is_open && now.isBefore(nextClose)) {
              return {
                label,
                subLabel: `Expires at ${nextClose.format("h:mm a")} ET today`,
                value,
              };
            }
            if (!clock.is_open && now.isBefore(nextClose)) {
              return {
                label,
                subLabel: `Expires ${nextClose.format("MM/DD h:mm a")} ET`,
                value,
              };
            }
          }
        }

        return {
          label,
          subLabel,
          value,
        };
      })
  );
};

export const useTimeInForceOptions = ({
  orderType,
  quantityType,
  isFractional,
  clock,
  isExtendedHours,
  isTokenizedEquity = false,
}: UseTimeInForceOptionsProps) => {
  const timeInForceOptions = useMemo(
    () =>
      getTimeInForceOptions({
        orderType,
        quantityType,
        isFractional,
        clock,
        isExtendedHours,
        isTokenizedEquity,
      }),
    [
      orderType,
      quantityType,
      isFractional,
      clock,
      isExtendedHours,
      isTokenizedEquity,
    ],
  );

  return { timeInForceOptions };
};
